import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, XCircle, Percent } from "lucide-react";

export default function Approvals() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const [decide, setDecide] = useState(null); // {order, action}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback((t = tab) => api.get(`/admin/approvals?status=${t}`).then((r) => setRows(r.data)), [tab]);
  useEffect(() => { setRows(null); load(tab); }, [tab, load]);

  const act = async (withCommission) => {
    setBusy(true);
    try {
      const path = decide.action === "reject" ? "reject" : "approve";
      await api.post(`/admin/orders/${decide.order.id}/${path}`, { note, with_commission: withCommission });
      toast.success(decide.action === "reject" ? `${decide.order.invoice_no} rejected - commission voided` : `${decide.order.invoice_no} approved ${withCommission ? "with" : "without"} commission`);
      setDecide(null); setNote("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Discount Approvals"
        subtitle="Invoices discounted above the 5% policy limit wait here. Commission (and the sale itself) only count towards the agent's month once approved."
      />
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map((t) => (
          <Button key={t} size="sm" variant={tab === t ? "default" : "outline"} onClick={() => setTab(t)} className="capitalize" data-testid={`approvals-tab-${t}`}>{t}</Button>
        ))}
      </div>
      {!rows ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : rows.length === 0 ? <Empty>No {tab} invoices above 5% discount.</Empty> : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left font-medium px-4 py-3">Invoice</th>
                    <th className="text-left font-medium px-4 py-3">Date</th>
                    <th className="text-left font-medium px-4 py-3">Agent</th>
                    <th className="text-left font-medium px-4 py-3">Customer</th>
                    <th className="text-right font-medium px-4 py-3">Max disc.</th>
                    <th className="text-right font-medium px-4 py-3">List value</th>
                    <th className="text-right font-medium px-4 py-3">Invoice value</th>
                    <th className="text-right font-medium px-4 py-3">Discount given</th>
                    <th className="text-right font-medium px-4 py-3">Comm. if approved</th>
                    <th className="text-left font-medium px-4 py-3">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`approval-row-${o.id}`}>
                      <td className="px-4 py-3 font-mono text-xs"><Link to={`/invoice/${o.id}`} className="text-primary hover:underline">{o.invoice_no}</Link></td>
                      <td className="px-4 py-3 text-muted-foreground">{o.date}</td>
                      <td className="px-4 py-3">{o.agent_name}</td>
                      <td className="px-4 py-3">{o.customer_name}</td>
                      <td className="px-4 py-3 text-right"><Badge variant="destructive" className="gap-1"><Percent className="w-3 h-3" />{o.max_discount_level}%</Badge></td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{money(o.list_value)}</td>
                      <td className="px-4 py-3 text-right">{money(o.sale_value)}</td>
                      <td className="px-4 py-3 text-right text-destructive">−{money(o.discount_given)}</td>
                      <td className="px-4 py-3 text-right text-primary">{money(o.commission_if_approved)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={o.approval_status === "approved" ? "default" : o.approval_status === "rejected" ? "destructive" : "secondary"} className="capitalize">{o.approval_status}{o.approval_status === "approved" && o.commission_void && " · no comm."}</Badge>
                        {o.approval?.by && <p className="text-[11px] text-muted-foreground mt-1">{o.approval.by} · {o.approval.at?.slice(0, 10)}{o.approval.note && ` · "${o.approval.note}"`}</p>}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {o.approval_status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => setDecide({ order: o, action: "approve" })} data-testid={`approve-${o.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDecide({ order: o, action: "reject" })} data-testid={`reject-${o.id}`}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      <Dialog open={!!decide} onOpenChange={(v) => !v && setDecide(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{decide?.action === "reject" ? "Reject" : "Approve"} {decide?.order.invoice_no} · {decide?.order.max_discount_level}% discount</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {decide?.action === "reject"
              ? "The invoice stays on record for stock and payment tracking, but the agent earns no commission on it."
              : `Policy: discounts beyond 5% may void commission on the order. Commission if paid with commission: ${money(decide?.order.commission_if_approved || 0)}.`}
          </p>
          <Input placeholder="Note for the record (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-note-input" />
          <DialogFooter className="gap-2">
            {decide?.action === "reject" ? (
              <Button variant="destructive" disabled={busy} onClick={() => act(false)} data-testid="confirm-reject-button">Reject invoice</Button>
            ) : (
              <>
                <Button variant="outline" disabled={busy} onClick={() => act(false)} data-testid="approve-no-commission-button">Approve · void commission</Button>
                <Button disabled={busy} onClick={() => act(true)} data-testid="approve-with-commission-button">Approve · with commission</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
